import { ListAltOutlined, RequestQuoteOutlined } from '@mui/icons-material';
import { Dialog } from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { I18nNamespace } from '~/common/types';
import { GuidedDialogAction } from '~/components/GuidedDialog/components/GuidedDialogAction/GuidedDialogAction';
import { GuidedDialog } from '~/components/GuidedDialog/GuidedDialog';

import { NegotiationCaseCreateSimpleDialog } from './components/LegacyNegotiationCaseCreate/LegacyNegotiationCaseCreate';
import { NegotiationCaseCreateWizard } from './components/NegotiationCaseCreateWizard/NegotiationCaseCreateWizard';

enum GuideOptions {
  WITH_QUOTE = 'SelectQuote',
  ONLY_CASE = 'RefineQuoteDetails',
}

interface NegotiationCaseCreateWizardProps {
  onClose: () => void;
  negotiationCaseCount?: number;
  open: boolean;
}

export const NegotiationCaseCreateTrack = ({
  onClose,
  negotiationCaseCount = 0,
  open,
}: NegotiationCaseCreateWizardProps) => {
  const { hasPermission } = usePermissions();

  const [chosenOption, setOption] = useState<GuideOptions | undefined>();

  const { t } = useTranslation(I18nNamespace.demand, { keyPrefix: 'NegotiationCaseCreate.guided' });

  const extendedCloseDialog = useCallback(() => {
    onClose();
    setOption(undefined);
  }, [onClose, setOption]);

  const needsGuidedDialog = hasPermission(Permission.DemandAnalyseDocument) && !chosenOption;

  const renderContent = () => {
    if (chosenOption === GuideOptions.WITH_QUOTE) {
      return <NegotiationCaseCreateWizard onClose={extendedCloseDialog} />;
    }

    if (needsGuidedDialog) {
      return (
        <GuidedDialog closeDialog={extendedCloseDialog} title={t('title')}>
          <GuidedDialogAction
            title={t('quoteOption.title')}
            description={t('quoteOption.description')}
            onClick={() => {
              setOption(GuideOptions.WITH_QUOTE);
            }}
            startIcon={<RequestQuoteOutlined />}
            disabled={negotiationCaseCount > 0}
            disabledTooltipTitle={t('quoteOption.disableTooltipTitle')}
          />
          <GuidedDialogAction
            title={t('createCaseOption.title')}
            description={t('createCaseOption.description')}
            onClick={() => {
              setOption(GuideOptions.ONLY_CASE);
            }}
            startIcon={<ListAltOutlined />}
          />
        </GuidedDialog>
      );
    }

    return <NegotiationCaseCreateSimpleDialog closeDialog={extendedCloseDialog} />;
  };
  return (
    <Dialog open={open} fullWidth maxWidth={'lg'} PaperProps={{ sx: { minHeight: 300 } }}>
      {renderContent()}
    </Dialog>
  );
};
